import React, { useState, useEffect, useCallback } from 'react';
import { platnosciService, formaLabel, komunikatBledu } from '../../services/platnosciService';
import { formatCurrency, getPaymentTypeColor } from './kasaBankHelpers';
import ZmienPlatnoscModal from './ZmienPlatnoscModal';

const BRAK_ZMIANY = 'Brak otwartej zmiany - zmiana płatności niedostępna';

const hhmm = (czas) => (czas ? String(czas).slice(0, 5) : '-');

/**
 * Zakladka Kasa/Bank "Platnosci zmiany": transakcje OTWARTEJ zmiany biezacego
 * uzytkownika z akcja "Zmien platnosc" (tylko gdy backend zwroci mozna_zmienic).
 *
 * @param {number}   locationId
 * @param {Function} onZmieniono  po udanej zmianie (odswiezenie salda - loadData z useKasaBankData)
 */
function PlatnosciZmianyTab({ locationId, onZmieniono }) {
  const [transakcje, setTransakcje] = useState([]);
  const [zmiana, setZmiana] = useState(null);
  const [komunikat, setKomunikat] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wybrana, setWybrana] = useState(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError('');
    try {
      const res = await platnosciService.transakcjeZmiany(locationId);
      setTransakcje(res.transakcje || []);
      setZmiana(res.zmiana || null);
      setKomunikat(res.komunikat || (res.zmiana ? '' : BRAK_ZMIANY));
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      setError(komunikatBledu(err, 'Błąd pobierania transakcji zmiany'));
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  const handleSuccess = () => {
    setWybrana(null);
    load();
    onZmieniono && onZmieniono();
  };

  const cellHead = {
    padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)',
    textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap'
  };
  const cell = { padding: '8px 10px', fontSize: '12px', color: 'var(--text)', verticalAlign: 'middle' };

  return (
    <div>
      {/* Naglowek zmiany */}
      <div style={{
        marginBottom: '1rem', padding: '0.75rem 1rem',
        backgroundColor: zmiana ? 'var(--success-light)' : 'var(--warning-light)',
        border: `1px solid ${zmiana ? 'var(--success)' : 'var(--warning)'}`,
        borderRadius: '0.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'
      }}>
        {zmiana ? (
          <div style={{ fontSize: '12px', color: 'var(--success)' }} data-testid="naglowek-zmiany">
            <i className="fas fa-door-open" style={{ marginRight: '0.5rem' }}></i>
            <strong>Otwarta zmiana</strong> — kasjer <strong>{zmiana.kasjer_login || '-'}</strong>,
            {' '}od <strong>{hhmm(zmiana.czas_rozpoczecia)}</strong>
            {zmiana.data_zmiany ? ` (${zmiana.data_zmiany})` : ''}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--warning)' }} data-testid="naglowek-zmiany">
            <i className="fas fa-lock" style={{ marginRight: '0.5rem' }}></i>
            {komunikat || BRAK_ZMIANY}
          </div>
        )}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)',
            backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)',
            cursor: loading ? 'wait' : 'pointer', fontWeight: '500'
          }}
        >
          <i className="fas fa-sync-alt" style={{ marginRight: '0.4rem' }}></i>
          {loading ? 'Ładowanie...' : 'Odśwież'}
        </button>
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
        Zmiana formy płatności jest możliwa tylko dla transakcji z otwartej zmiany
        (czysta forma: gotówka / karta / BLIK / przelew, bez kuponów, faktur i zwrotów). Po zamknięciu zmiany — niedostępna.
      </div>

      {error && (
        <div role="alert" style={{
          color: 'var(--danger)', background: 'var(--danger-light)', border: '1px solid var(--danger-light)',
          borderRadius: '4px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '11px'
        }}>
          {error}
        </div>
      )}

      {/* Tabela */}
      {transakcje.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '12px' }}>
          {loading ? 'Ładowanie...' : (zmiana ? 'Brak transakcji w otwartej zmianie' : 'Brak transakcji do wyświetlenia')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)' }}>
                <th style={cellHead}>Godzina</th>
                <th style={cellHead}>Paragon</th>
                <th style={cellHead}>Kasjer</th>
                <th style={{ ...cellHead, textAlign: 'right' }}>Kwota</th>
                <th style={cellHead}>Forma</th>
                <th style={{ ...cellHead, textAlign: 'center' }}>Fiskalny</th>
                <th style={{ ...cellHead, textAlign: 'right' }}>Akcja</th>
              </tr>
            </thead>
            <tbody>
              {transakcje.map((t, idx) => {
                const mozna = !!t.mozna_zmienic;
                const kolor = getPaymentTypeColor(t.forma_platnosci);
                return (
                  <tr
                    key={t.id}
                    data-testid={`wiersz-${t.id}`}
                    style={{
                      backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)',
                      borderBottom: '1px solid var(--border)'
                    }}
                  >
                    <td style={{ ...cell, fontFamily: 'monospace' }}>{hhmm(t.czas)}</td>
                    <td style={{ ...cell, fontFamily: 'monospace', fontSize: '11px' }}>
                      {t.numer_paragonu || t.numer_transakcji || `#${t.id}`}
                    </td>
                    <td style={cell}>{t.kasjer_login || '-'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{formatCurrency(t.kwota)}</td>
                    <td style={cell}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
                        fontSize: '11px', fontWeight: '600',
                        backgroundColor: 'var(--bg)', color: kolor, border: `1px solid ${kolor}`
                      }}>
                        {formaLabel(t.forma_platnosci)}
                      </span>
                      {t.ostatnia_korekta && (
                        <div
                          style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}
                          title={`Korekta: ${t.ostatnia_korekta.kto || ''} ${t.ostatnia_korekta.kiedy || ''}`}
                        >
                          korekta: {formaLabel(t.ostatnia_korekta.z)} → {formaLabel(t.ostatnia_korekta.na)}
                        </div>
                      )}
                    </td>
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <span style={{
                        fontSize: '12px', fontWeight: 'bold',
                        color: t.fiskalizacja ? 'var(--success)' : 'var(--text-muted)'
                      }} title={t.fiskalizacja ? 'Paragon zafiskalizowany' : 'Niezafiskalizowany'}>
                        {t.fiskalizacja ? 'F' : '-'}
                      </span>
                    </td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <button
                        type="button"
                        disabled={!mozna}
                        title={mozna ? 'Zmień formę płatności tej transakcji' : (t.powod_blokady || 'Zmiana płatności niedostępna')}
                        onClick={() => mozna && setWybrana(t)}
                        style={{
                          padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                          backgroundColor: mozna ? 'var(--info)' : 'var(--bg-secondary)',
                          color: mozna ? '#fff' : 'var(--text-muted)',
                          border: mozna ? '1px solid var(--info)' : '1px solid var(--border)',
                          cursor: mozna ? 'pointer' : 'not-allowed',
                          opacity: mozna ? 1 : 0.7,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        💱 Zmień płatność
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ZmienPlatnoscModal
        isOpen={!!wybrana}
        onClose={() => setWybrana(null)}
        onSuccess={handleSuccess}
        transakcja={wybrana}
      />
    </div>
  );
}

export default PlatnosciZmianyTab;
